import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ActionPlanViewer from '../ActionPlanViewer';
import { useChat } from 'ai/react';
import useChatPanelStore from 'stateManagement/store/useChatPanelStore';

import { useGetSessionById } from 'stateManagement/queries/services/aiChat/aiChatQueries';

import {
  selectActionPlanById,
  selectActionPlans,
} from '../../../../../stateManagement/redux/actionPlans/selector';
import { ACTION_PLAN_STATUSES } from 'stateManagement/redux/actionPlans/types';

import ChatInput from '../../components/ChatInput';
import ChatMessages from '../../components/chatMessages/ChatMessages';
import WorkflowButtons from '../../workflows/components/WorkflowButtons';

const PanelContent = () => {
  /**
   * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
   */
  const [isActionPlanMessage, setIsActionPlanMessage] = useState(false);

  const routeParams = useParams();
  const chatSessionId = useChatPanelStore((state) => state.chatSessionId);
  const { data: chatData } = useGetSessionById(chatSessionId);

  // Get action plans from Redux
  const plans = useSelector(selectActionPlans);

  const currentPlan = useSelector((state) => {
    // Find the active plan for this chat session
    const planForSession = plans.find((p) => p.chatSessionId === chatSessionId);
    return selectActionPlanById(state, planForSession?.id);
  });

  const {
    messages,
    setMessages,
    input,
    setInput,
    stop,
    handleInputChange,
    handleSubmit,
    append,
    status,
  } = useChat({
    id: chatSessionId || undefined,
    initialMessages: chatData?.messages || [],
    streamProtocol: 'text',
    api: `${process.env.REACT_APP_BACKEND_URL}/api/v2/ai-chat/message/${chatSessionId}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    body: {
      // Set service to "default" if not an action plan message, otherwise use the current plan's name
      service:
        isActionPlanMessage && currentPlan?.name
          ? currentPlan.name
          : '0-default',
      data: routeParams,
    },
  });

  const scrollToBottom = (smooth = true) => {
    const parentDiv = document.querySelector('#scrollable-panel-content');
    if (parentDiv) {
      parentDiv.style.overflow = 'auto';
      parentDiv.scrollTo({
        top: parentDiv.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  };

  useEffect(() => {
    if (chatData) {
      scrollToBottom(false);
    }
  }, [chatData]);

  return chatData?.sessionId === chatSessionId ? (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        id='scrollable-panel-content'
        className='mb-6'
        style={{ flex: 1, overflow: 'auto', paddingBottom: '80px' }}
      >
        {/* hide top workflow buttons if chat has messages */}
        {messages?.length > 0 ? null : (
          <div className='mb-2 mt-2'>
            <WorkflowButtons />
          </div>
        )}

        <ChatMessages
          status={status}
          messages={messages}
          sessionId={chatSessionId}
          user={chatData?.createdBy}
        />
        <ActionPlanViewer />

        {status === 'submitted' && (
          <div className='d-flex align-items-center mb-2'>
            <Spinner className='me-2' size='sm' variant='primary' />
            Thinking about it...
          </div>
        )}
      </div>

      <ChatInput
        sessionId={chatSessionId}
        input={input}
        setInput={setInput}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        append={append}
        stop={stop}
        isStreaming={status === 'streaming'}
        setMessages={setMessages}
        messages={messages}
        setIsActionPlanMessage={setIsActionPlanMessage}
        isDisabled={
          status === 'submitted' ||
          status === 'streaming' ||
          (currentPlan &&
            currentPlan.status !== ACTION_PLAN_STATUSES.COMPLETED &&
            currentPlan.status !== ACTION_PLAN_STATUSES.FAILED)
        }
      />
    </div>
  ) : (
    <div className='text-center text-500 fw-medium'>No Chat Selected</div>
  );
};

export default PanelContent;
